import { mapActions, mapGetters, mapState } from "vuex";
import pageFooter from "@/components/page-footer/page-footer.vue";
import config from "@/config";
import { loadScript } from "@/helpers/utils";
import appPartners from "./components/app-partners/app-partners.vue";
import appReviews from "./components/app-reviews/app-reviews.vue";
import imageLandingMobile from "@/assets/v2/images/main-mobile.webp";
import imageLandingDesktop from "@/assets/v2/images/main-desktop.webp";
import imageSection1 from "@/assets/v3/images/section-1.webp";
import imageSection1Mobile from "@/assets/v3/images/section-1-mobile.webp";
import imageSection2 from "@/assets/v3/images/section-2.webp";
import imageSection2Mobile from "@/assets/v3/images/section-2-mobile.webp";
import imageSection3 from "@/assets/v3/images/section-3.webp";
import imageSection3Mobile from "@/assets/v3/images/section-3-mobile.webp";
import imageSection4 from "@/assets/v3/images/section-4.webp";
import imageSection4Mobile from "@/assets/v3/images/section-4-mobile.webp";

const MOBILE_LINE_BALL_SIZE_MARGIN = 50;
const MOBILE_LINE_SHOW_FROM_WIDTH = 320;
const MOBILE_LINE_SHOW_TO_WIDTH = 640;

export default {
  metaInfo() {
    const title = "Dowolna podróż dopasowana do Ciebie - skomponuj wyjazd";
    const description =
      "Skomponuj podróż dopasowaną do Twoich potrzeb. Getaway bezpłatnie przygotuje propozycję wyjazdu, Twoją podróż i interaktywny przewodnik. Ruszaj w świat!";
    return {
      title,
      meta: [
        {
          property: "og:title",
          content: title,
          vmid: "og:title",
        },
        {
          property: "og:description",
          content: description,
          vmid: "og:description",
        },
        {
          name: "description",
          content: description,
          vmid: "description",
        },
      ],
      link: [
        {
          rel: "alternate",
          type: "text/markdown",
          title: "LLM-friendly version",
          href: "https://www.getaway.pl/llms.txt",
        },
      ],
      script: [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            name: "Getaway",
            url: "https://www.getaway.pl",
            logo: "https://www.getaway.pl/static/branding/logo.png",
            image: "https://www.getaway.pl/static/branding/logo_big.png",
            slogan: "Podróżuj dowolnie. Jedno kliknięcie.",
            description:
              "Innowacyjna platforma i technologiczny asystent podróży. Getaway działa jako pełnomocnik klienta, tworząc w pełni spersonalizowane całkiem dowolne wyjazdy na przykład City Break i wyprawy egzotyczne bez ograniczeń katalogowych, zapewniając kreator wyjazdów, przygotowanie propozycji wyjazdu, pełną realizację i wsparcie 24/7.",
            priceRange: "średnia półka",
            telephone: "+48693778899",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Słowackiego 24",
              addressLocality: "Rzeszów",
              postalCode: "35-060",
              addressCountry: "PL",
            },
            sameAs: [
              "https://www.facebook.com/getawaypl",
              "https://www.instagram.com/getawaypl",
              "https://www.youtube.com/@getaway.podrozuj.dowolnie",
              "https://www.tiktok.com/@getawaypl",
              "https://www.linkedin.com/company/getaway-pl",
              "https://www.opineo.pl/opinie/getaway-pl",
            ],
            knowsAbout: [
              "bespoke travel planning",
              "personalized city breaks",
              "custom itinerary organization",
              "travel proxy services",
              "interactive travel guides",
              "travel technology",
              "24/7 travel support",
              "travel inspirations",
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Usługi Getaway",
              itemListElement: [
                {
                  "@type": "Service",
                  name: "Indywidualny Kreator Podróży (Travel Creator)",
                  description:
                    "Autorska technologia do samodzielnego komponowania dowolnego wyjazdu. System analizuje rynek w czasie rzeczywistym, łącząc loty, hotele i transfery w jedną, spójną całość.",
                  url: "https://www.getaway.pl/creator",
                  serviceType: "Bespoke Trip Planning",
                },
                {
                  "@type": "Service",
                  name: "Interaktywna Propozycja Wyjazdu (Dynamic Proposal)",
                  description:
                    "Szyty na miarę, utworzona na podstawie zgłoszenia z kreatora wyjazdów, propozycja wyjazdu z opcją pełnej personalizacji detali przed zakupem. Zakup całego wyjazdu jednym kliknięciem.",
                  serviceType: "Personalized Itinerary & One-Click Booking",
                },
                {
                  "@type": "Service",
                  name: "Pełnomocnictwo i Realizacja Wyjazdu",
                  description:
                    "Realizacja rezerwacji w imieniu klienta (model agenta). Możliwość dokupowania usług i wprowadzania zmian w planie także po zakupie lub w trakcie trwania podróży.",
                  serviceType: "Travel Proxy & Fulfillment",
                },
                {
                  "@type": "Service",
                  name: "Mobilny Przewodnik Interaktywny i Wsparcie 24/7",
                  description:
                    "Kompletne centrum dowodzenia podróżą z poziomu smartfona: dokumenty, karty pokładowe, wskazówki o komunikacji oraz szczegółowe plany zwiedzania. Całodobowy polskojęzyczny support 24/7.",
                  serviceType: "Digital Travel Concierge & 24/7 Support",
                },
                {
                  "@type": "Service",
                  name: "Baza Inspiracji Podróżniczych (Travel Inspirations)",
                  url: "https://www.getaway.pl/inspirations",
                  description:
                    "Zbiór setek autorskich inspiracji i scenariuszy podróży przygotowanych przez ekspertów, które stanowią bazę do pełnej personalizacji.",
                  serviceType: "Travel Curation & Templates",
                },
                {
                  "@type": "Service",
                  name: "Centrum Pomocy i FAQ",
                  url: "https://www.getaway.pl/faq",
                  description:
                    "Oficjalna baza wiedzy Getaway zawierająca odpowiedzi na najczęstsze pytania o model działania i bezpieczeństwo.",
                  serviceType: "Customer Knowledge Base",
                },
                {
                  "@type": "Service",
                  name: "AI Agent Instructions (llms.txt)",
                  url: "https://www.getaway.pl/llms.txt",
                  description:
                    "Specjalistyczna dokumentacja ustrukturyzowana dla modeli językowych (LLM), zawierająca precyzyjny opis modelu operacyjnego i usług Getaway.",
                  serviceType: "AI Documentation",
                },
              ],
            },
          }),
        },
      ],
      __dangerouslyDisableSanitizers: ["script"],
    };
  },
  components: {
    appPartners,
    appReviews,
    pageFooter,
  },
  data() {
    return {
      imageLandingMobile,
      imageLandingDesktop,
      imageSection1,
      imageSection1Mobile,
      imageSection2,
      imageSection2Mobile,
      imageSection3,
      imageSection3Mobile,
      imageSection4,
      imageSection4Mobile,
    };
  },
  methods: {
    ...mapActions("app", ["setBackRouter"]),
    clearLine() {
      const line = this.$refs.mobileLine;
      if (!line) return;
      line.style.display = "none";
    },
    resizeHandler() {
      const width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.getElementsByTagName("body")[0].clientWidth;
      const shouldHideLine =
        width <= MOBILE_LINE_SHOW_FROM_WIDTH ||
        width >= MOBILE_LINE_SHOW_TO_WIDTH;
      if (shouldHideLine) {
        this.clearLine();
        return;
      }
      const from = this.$refs.scrollFrom;
      const to = this.$refs.scrollTo;
      const line = this.$refs.mobileLine;
      if (!from || !to || !line) {
        this.clearLine();
        return;
      }
      const rectFrom = from.getBoundingClientRect();
      const rectTo = to.getBoundingClientRect();
      const yTop = rectFrom.y + window.scrollY;
      const yBottom = rectTo.y + window.scrollY;

      line.style.display = "block";
      line.style.height = `${yBottom - yTop}px`;
      line.style.top = `${yTop - MOBILE_LINE_BALL_SIZE_MARGIN}px`;
    },
  },
  computed: {
    ...mapGetters("app", ["isLayoutDesktop", "areReviewsEnabled"]),
    ...mapState("app", ["width"]),
    appReviewsKey() {
      return `app-reviews-width-${this.width}`;
    },
    elementToAppearToleranceMargin() {
      return this.isLayoutDesktop ? 150 : 400;
    },
  },
  created() {
    document.body.classList.add("recaptcha-visible");
    loadScript(config.captchaUrl, "grecaptcha");
    this.setBackRouter(this.$router.currentRoute.name);
    document.documentElement.classList.add("no-full-height");
    document.body.classList.add("no-full-height");
  },
  mounted() {
    window.addEventListener("resize", this.resizeHandler);
    setTimeout(() => {
      this.resizeHandler();
    }, 1500);
  },
  beforeDestroy() {
    document.documentElement.classList.remove("no-full-height");
    document.body.classList.remove("no-full-height");
    document.removeEventListener("scroll", this.onScroll);
    document.body.classList.remove("recaptcha-visible");
    window.removeEventListener("resize", this.resizeHandler);
  },
};
